import { useState } from "react";
import { ArrowLeft, Send, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

// 送信先は同じサイト内の Cloudflare Pages Functions（frontend/functions/api/contact.js）
const API = "/api";

export default function ContactPage() {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    company: "",
    email: "",
    phone: "",
    message: "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success("お問い合わせを送信しました");
    } catch (error) {
      console.error("Error submitting contact form:", error);
      toast.error("送信に失敗しました。もう一度お試しください。");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isSubmitted) {
    return (
      <div className="pt-16 md:pt-20 min-h-screen bg-white" data-testid="contact-page">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200"
            data-testid="back-to-home"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Home</span>
          </Link>
        </div>

        <div className="flex items-center justify-center py-24 md:py-32">
          <div className="text-center max-w-md mx-auto px-6">
            <div className="w-16 h-16 bg-green-50 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-500" />
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4">
              お問い合わせありがとうございます
            </h1>
            <p className="text-gray-600 mb-8">
              内容を確認の上、担当者よりご連絡させていただきます。
            </p>
            <Link
              to="/"
              className="inline-flex items-center justify-center bg-black text-white hover:bg-gray-800 rounded-full px-8 py-3 text-sm font-medium transition-all duration-300"
              data-testid="return-home-button"
            >
              トップページへ戻る
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-16 md:pt-20 min-h-screen bg-white" data-testid="contact-page">
      {/* Back Link */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-8">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900 transition-colors duration-200"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Back to Home</span>
        </Link>
      </div>

      {/* Header */}
      <section className="py-12 md:py-16 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <h1 className="font-en text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Contact
          </h1>
          <p className="text-gray-600">
            お問い合わせ・ご相談はこちらからお気軽にどうぞ。
            <br />
            <span className="text-xs text-gray-400">※営業を目的としたお問い合わせはお控えください。</span>
          </p>
        </div>
      </section>

      {/* Contact Form */}
      <section className="py-16 md:py-24">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <form onSubmit={handleSubmit} className="space-y-8" data-testid="contact-form">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium text-gray-700">
                お名前 <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="山田 太郎"
                className="bg-gray-50 border-transparent focus:border-gray-300 focus:bg-white focus:ring-0 rounded-lg px-4 py-3"
                data-testid="contact-name-input"
              />
            </div>

            {/* Company */}
            <div className="space-y-2">
              <Label htmlFor="company" className="text-sm font-medium text-gray-700">
                会社名
              </Label>
              <Input
                id="company"
                name="company"
                type="text"
                value={formData.company}
                onChange={handleChange}
                placeholder="株式会社〇〇"
                className="bg-gray-50 border-transparent focus:border-gray-300 focus:bg-white focus:ring-0 rounded-lg px-4 py-3"
                data-testid="contact-company-input"
              />
            </div>

            {/* Email */}
            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-gray-700">
                メールアドレス <span className="text-red-500">*</span>
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="example@example.com"
                className="bg-gray-50 border-transparent focus:border-gray-300 focus:bg-white focus:ring-0 rounded-lg px-4 py-3"
                data-testid="contact-email-input"
              />
            </div>

            {/* Phone */}
            <div className="space-y-2">
              <Label htmlFor="phone" className="text-sm font-medium text-gray-700">
                電話番号
              </Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="03-1234-5678"
                className="bg-gray-50 border-transparent focus:border-gray-300 focus:bg-white focus:ring-0 rounded-lg px-4 py-3"
                data-testid="contact-phone-input"
              />
            </div>

            {/* Message */}
            <div className="space-y-2">
              <Label htmlFor="message" className="text-sm font-medium text-gray-700">
                お問い合わせ内容 <span className="text-red-500">*</span>
              </Label>
              <Textarea
                id="message"
                name="message"
                required
                value={formData.message}
                onChange={handleChange}
                placeholder="お問い合わせ内容をご記入ください"
                rows={6}
                className="bg-gray-50 border-transparent focus:border-gray-300 focus:bg-white focus:ring-0 rounded-lg px-4 py-3 resize-none"
                data-testid="contact-message-input"
              />
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <Button
                type="submit"
                disabled={isSubmitting}
                className="w-full bg-black text-white hover:bg-gray-800 rounded-full px-8 py-3 text-sm font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="contact-submit-button"
              >
                {isSubmitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle 
                        className="opacity-25" 
                        cx="12" 
                        cy="12" 
                        r="10" 
                        stroke="currentColor" 
                        strokeWidth="4"
                        fill="none"
                      />
                      <path 
                        className="opacity-75" 
                        fill="currentColor" 
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      />
                    </svg>
                    送信中...
                  </span>
                ) : (
                  <span className="flex items-center justify-center gap-2">
                    <Send className="w-4 h-4" />
                    送信する
                  </span>
                )}
              </Button>
            </div>
          </form>
        </div>
      </section>
    </div>
  );
}
